// src/pages/DiagramGenerator.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, MenuItem, LinearProgress, Box
} from "@mui/material";
import { ContentBuilderPage } from "../components/ContentBuilderPage";
import { useAxiosAuth } from "../hooks/useAxiosAuth";
import { ACCURACY_LEVELS } from "../constants/accuracyLevels";
import { DIAGRAM_TYPES } from "../constants/diagramTypes";

/* --- Modal just handles form state --- */
function DiagramGeneratorModal({ open, onCancel, onGenerate, initialText }) {
  // NEW: keep the text in state so it can be edited
  const [textToVisualize, setTextToVisualize] = useState(initialText?.length > 0 ? initialText : "Describe diagram to be generated");
  const [vizType, setVizType] = useState("diagram");
  const [vizSubtype, setVizSubtype] = useState(DIAGRAM_TYPES.NODE);
  const [vizName, setVizName] = useState("diagram");
  const [loading, setLoading] = useState(false);
  const [accuracyLevel, setAccuracyLevel] = useState(ACCURACY_LEVELS.ADVANCED);
  
  // If initialText ever changes from the parent, sync it
  useEffect(() => {
    setTextToVisualize(initialText?.length > 0 ? initialText : "Describe diagram to be generated");
  }, [initialText]);

  const handleSubmit = async () => {
    setLoading(true);
    // now pass the edited text back up, too
    try {
      await onGenerate(textToVisualize, { vizName, vizType, vizSubtype, accuracyLevel });
    } catch (e) {
      console.error(e)
      //setError(e);  
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Clickable Diagram Generator</DialogTitle>
      <DialogContent sx={{ p: 2, display: "flex", flexDirection: "column" }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* scrollable text area */}
        <Box sx={{ flex: "1 1 auto", maxHeight: "40vh", overflowY: "auto" }}>
          <TextField
            fullWidth
            multiline
            // no longer disabled
            value={textToVisualize}
            onChange={e => setTextToVisualize(e.target.value)}
            margin="normal"
          />
        </Box>

        {/* sticky controls */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            bgcolor: "background.paper",
            pt: 2,
            pb: 1,
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2
          }}
        >
          <TextField
            label="Diagram Name"
            fullWidth
            value={vizName}
            onChange={e => setVizName(e.target.value)}
            margin="normal"
          />
          <TextField
            select
            label="Diagram Type"
            fullWidth
            value={vizSubtype}
            onChange={e => setVizSubtype(e.target.value)}
            margin="normal"
          >
            <MenuItem value={DIAGRAM_TYPES.NODE}>Node Diagram</MenuItem>
            <MenuItem value={DIAGRAM_TYPES.STATE}>State Diagram</MenuItem>
            <MenuItem value={DIAGRAM_TYPES.TABBED}>Tabbed Diagram</MenuItem>
            <MenuItem value={DIAGRAM_TYPES.PLACEHOLDER}>Placeholder</MenuItem>
          </TextField>
          {/* new accuracy level selector */}
          <TextField
            select
            label="Accuracy Level"
            fullWidth
            value={accuracyLevel}
            onChange={(e) => setAccuracyLevel(e.target.value)}
            margin="normal"
          >
            <MenuItem value={ACCURACY_LEVELS.STANDARD}>Standard LLM</MenuItem>
            <MenuItem value={ACCURACY_LEVELS.ADVANCED}>Advanced LLM</MenuItem>
          </TextField>

        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* --- Page component --- */
export default function DiagramGenerator() {
  const axios = useAxiosAuth();

  const generateVisualization = useCallback(
    async (text, courseId, lessonId, { vizName, vizType, vizSubtype, accuracyLevel }) => {
      const body = {
        concept: text,
        course_id: courseId,
        lesson_id: lessonId,
        visual_type: vizType,
        visual_subtype: vizSubtype,
        visual_name: vizName,
        accuracy_level:accuracyLevel
      };
      const { data } = await axios.post("/visuals/generate", body);
      return data;
    },
    [axios]
  );

  return (
    <ContentBuilderPage
      instructionText="Highlight text to create an interactive diagram."
      modal={<DiagramGeneratorModal />}
      generatorFn={generateVisualization}
      assetType="visuals"
    />
  );
}
